import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { Link } from "wouter";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { AnimatePresence, motion, useDragControls, useReducedMotion, type PanInfo } from "framer-motion";
import { X } from "lucide-react";
import { MomentumCard } from "@/components/momentum/momentum-card";
import { folderTone, monthName } from "@/components/momentum/month-folder";
import { applyOrder, progressLabel, type MomentumItem, type MomentumMonth } from "@/lib/momentum";

const EASE_OUT = [0.22, 1, 0.36, 1] as const;
/** Letting go of a header pulled down this far (px), or flicked this fast (px/s), closes the sheet. */
const DISMISS_OFFSET = 110;
const DISMISS_VELOCITY = 650;
/** The "more steps" cue bows out once the list has moved this far (px). */
const CUE_SCROLL_DISMISS = 12;
const STAGGER_CAP = 6;

/** `rgb(r g b)` → the same colour at `alpha`, for fades that melt into the sheet. */
function withAlpha(rgb: string, alpha: number) {
  return rgb.replace(/\)$/, ` / ${alpha})`);
}

interface Props {
  /** The open month, or null when the sheet is closed. */
  month: MomentumMonth | null;
  order: string[] | undefined;
  onClose: () => void;
  onToggle: (item: MomentumItem) => void;
}

/**
 * Phones only: an open month as a bottom sheet over the folder grid, instead of the
 * inline panel desktop uses. Radix Dialog supplies the modal parts (focus, Escape, scroll
 * lock, inert background); framer-motion the rise, the fade and the pull-down dismiss.
 */
export function MomentumSheet({ month, onClose, ...rest }: Props) {
  return (
    <DialogPrimitive.Root open={!!month} onOpenChange={(open) => !open && onClose()}>
      <AnimatePresence>
        {month && <SheetBody key={month.key} month={month} onClose={onClose} {...rest} />}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}

function SheetBody({
  month,
  order,
  onClose,
  onToggle,
}: Omit<Props, "month"> & { month: MomentumMonth }) {
  const reduce = useReducedMotion();
  const dragControls = useDragControls();
  const sheetRef = useRef<HTMLDivElement>(null);
  const tone = folderTone(month);
  const items = applyOrder(month.items, order);
  const name = monthName(month.month);
  const total = month.items.length;
  const { scrollerRef, listRef, onScroll, scrolled, moreBelow, cueCount } = useScrollDiscovery(items.length);

  // Pull-down starts only from the grabber/header, never the card list, and never from
  // the close button (so a tap on X stays a tap).
  const startDrag = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest("button, a")) return;
    dragControls.start(e);
  };
  const onDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.y > DISMISS_OFFSET || (info.velocity.y > DISMISS_VELOCITY && info.offset.y > 24)) onClose();
  };

  return (
    <DialogPrimitive.Portal forceMount>
      <DialogPrimitive.Overlay asChild forceMount>
        <motion.div
          className="fixed inset-0 z-[60] bg-foreground/25 backdrop-blur-[2px]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.24 } }}
          exit={{ opacity: 0, transition: { duration: 0.22 } }}
        />
      </DialogPrimitive.Overlay>
      <DialogPrimitive.Content
        asChild
        forceMount
        aria-describedby={undefined}
        // Land focus on the sheet itself rather than lighting up the close button.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          sheetRef.current?.focus({ preventScroll: true });
        }}
      >
        <motion.div
          ref={sheetRef}
          id={`momentum-panel-${month.key}`}
          tabIndex={-1}
          // Shrinks to fit a short month; a long one stops at 78% of the visible screen
          // (and always clears the 64px app header), then only its list scrolls.
          className="fixed inset-x-0 bottom-0 z-[61] flex max-h-[78vh] flex-col overflow-hidden rounded-t-[28px] shadow-[0_-10px_40px_-12px_rgba(40,37,34,0.28)] outline-none supports-[height:1dvh]:max-h-[min(78dvh,calc(100dvh_-_76px))]"
          style={{ background: tone.panel }}
          initial={reduce ? { opacity: 0 } : { y: "100%" }}
          animate={
            reduce
              ? { opacity: 1, transition: { duration: 0.15 } }
              : { y: 0, transition: { type: "spring", stiffness: 380, damping: 40, mass: 0.9 } }
          }
          exit={
            reduce
              ? { opacity: 0, transition: { duration: 0.12 } }
              : { y: "100%", transition: { duration: 0.24, ease: [0.4, 0, 1, 1] } }
          }
          drag={reduce ? false : "y"}
          dragListener={false}
          dragControls={dragControls}
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={{ top: 0, bottom: 1 }}
          onDragEnd={onDragEnd}
        >
          <div
            onPointerDown={startDrag}
            className="shrink-0 touch-none select-none px-5 pb-4 pt-2.5 transition-shadow duration-200"
            style={{ boxShadow: scrolled ? "0 1px 0 rgba(40,37,34,0.07)" : undefined }}
          >
            <div aria-hidden className="mx-auto mb-3 h-1.5 w-10 rounded-full" style={{ background: tone.ink, opacity: 0.2 }} />
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <DialogPrimitive.Title className="font-display text-2xl font-semibold leading-tight tracking-[-0.015em] text-foreground">
                  {name} <span className="font-medium text-foreground/50">{month.year}</span>
                </DialogPrimitive.Title>
                {total > 0 && (
                  <p className="mt-1 text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: tone.ink }}>
                    Your momentum · {progressLabel(month.done, total).toLowerCase()}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label={`Close ${name}`}
                className="-mr-2 -mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-foreground/50 transition-colors hover:bg-white/70 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="relative flex min-h-0 flex-col">
            <div
              ref={scrollerRef}
              onScroll={onScroll}
              className="relative min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain px-4 pb-[max(env(safe-area-inset-bottom),20px)]"
            >
              {total === 0 ? (
                <p className="px-1 pb-2 text-sm leading-relaxed text-muted-foreground">
                  {month.isCurrent ? (
                    <>
                      Nothing gathered yet this month. The Next Steps from this month&apos;s Unfoldings will land here.{" "}
                      <Link href="/app" className="font-medium text-primary underline-offset-4 hover:underline">
                        Start an Unfolding
                      </Link>
                    </>
                  ) : (
                    <>A quiet month — no Next Steps were gathered in {name}.</>
                  )}
                </p>
              ) : (
                <ul ref={listRef} className="grid grid-cols-1 gap-3">
                  {items.map((item, i) => (
                    <motion.li
                      key={item.id}
                      className="min-w-0"
                      initial={reduce ? false : { opacity: 0, y: 8 }}
                      animate={{
                        opacity: 1,
                        y: 0,
                        transition: { delay: 0.12 + Math.min(i, STAGGER_CAP) * 0.03, duration: 0.28, ease: EASE_OUT },
                      }}
                    >
                      <MomentumCard item={item} onToggle={onToggle} stacked />
                    </motion.li>
                  ))}
                </ul>
              )}
            </div>

            {/* Scroll discovery: a soft fade while more sits below, plus a one-time count
                that bows out on the first small scroll. Decorative — the list itself is
                what screen readers walk. */}
            <AnimatePresence>
              {moreBelow && (
                <motion.div
                  key="fade"
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 bottom-0 h-20"
                  style={{ background: `linear-gradient(to bottom, ${withAlpha(tone.panel, 0)}, ${tone.panel} 85%)` }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                />
              )}
            </AnimatePresence>
            <AnimatePresence>
              {cueCount > 0 && (
                <motion.p
                  key="cue"
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 bottom-[max(env(safe-area-inset-bottom),12px)] text-center text-[12px] font-semibold tracking-[0.04em]"
                  style={{ color: tone.ink }}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 0.8, y: 0, transition: { delay: 0.35, duration: 0.25 } }}
                  exit={{ opacity: 0, transition: { duration: 0.18 } }}
                >
                  {cueCount === 1 ? "1 more step" : `${cueCount} more steps`} ↓
                </motion.p>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

/**
 * Reads the list's real scroll state: whether anything sits below the visible area, and
 * how many cards aren't fully in view (a peeking card counts). Uses layout offsets, not
 * rects, so the cards' entrance transforms don't skew the count.
 */
function useScrollDiscovery(itemCount: number) {
  // A callback ref, not useRef: Radix's Portal mounts its children a tick after this
  // component, so the scroller only exists once this state is set.
  const [scroller, scrollerRef] = useState<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const frame = useRef(0);
  const [scrolled, setScrolled] = useState(false);
  const [moreBelow, setMoreBelow] = useState(false);
  const [hidden, setHidden] = useState(0);
  const [cueDismissed, setCueDismissed] = useState(false);

  const measure = useCallback(() => {
    const el = scroller;
    if (!el) return;
    const below = el.scrollHeight - el.scrollTop - el.clientHeight > 8;
    let count = 0;
    if (below && listRef.current) {
      const edge = el.scrollTop + el.clientHeight + 1;
      for (const child of Array.from(listRef.current.children) as HTMLElement[]) {
        if (child.offsetTop + child.offsetHeight > edge) count++;
      }
    }
    setMoreBelow(below);
    setHidden(count);
    setScrolled(el.scrollTop > 0);
    if (el.scrollTop > CUE_SCROLL_DISMISS) setCueDismissed(true);
  }, [scroller]);

  useLayoutEffect(() => {
    if (!scroller) return;
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(scroller);
    if (listRef.current) ro.observe(listRef.current);
    return () => {
      ro.disconnect();
      cancelAnimationFrame(frame.current);
    };
  }, [scroller, measure, itemCount]);

  const onScroll = useCallback(() => {
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(measure);
  }, [measure]);

  return { scrollerRef, listRef, onScroll, scrolled, moreBelow, cueCount: cueDismissed || !moreBelow ? 0 : hidden };
}
